import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '@/stores/userStore';
import { Footer } from '@/components/Footer';

interface UserRoom {
  roomId: string;
  roomName: string;
  role: string;
  status: string;
  lastSeen: number;
}

export default function Home() {
  const [roomName, setRoomName] = useState('');
  const [loading, setLoading] = useState(false);
  const [myRooms, setMyRooms] = useState<UserRoom[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const navigate = useNavigate();
  const { userName, userSecret } = useUserStore();

  useEffect(() => {
    const fetchMyRooms = async () => {
      try {
        const response = await fetch('/api/users/rooms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userSecret }),
        });

        if (response.ok) {
          const data = await response.json();
          setMyRooms(data.rooms);
        }
      } catch (error) {
        console.error('Failed to fetch rooms:', error);
      } finally {
        setLoadingRooms(false);
      }
    };

    fetchMyRooms();
  }, [userSecret]);

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roomName.trim()) return;

    setLoading(true);
    try {
      const response = await fetch('/api/rooms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: roomName, ownerSecret: userSecret }),
      });

      const data = await response.json();

      if (response.ok) {
        navigate(`/room/${data.roomId}`);
      } else {
        alert('Failed to create room: ' + data.error);
      }
    } catch (error) {
      alert('Failed to create room');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoomClick = async (room: UserRoom) => {
    navigate(`/room/${room.roomId}`);
  };

  const getRoleDisplay = (role: string) => {
    switch (role) {
      case 'owner': return 'Owner';
      case 'dj': return 'DJ';
      default: return 'Listener';
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'owner': return 'badge-owner';
      case 'dj': return 'badge-dj';
      default: return 'badge-listener';
    }
  };

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-4xl mx-auto space-y-8 pt-8">
        {/* Header with user greeting */}
        <div className="text-center">
          <h1 className="text-4xl font-bold text-primary mb-2">
            <span className="text-lime-400">YT</span> Music Sync
          </h1>
          <p className="text-secondary">
            Welcome back, <span className="font-bold text-primary">{userName}</span>!
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Create/Join Room Card */}
          <div className="card space-y-6">
            <h2 className="text-xl font-bold text-primary">
              Create a Room
            </h2>
            <form onSubmit={handleCreateRoom} className="space-y-4">
              <div>
                <label htmlFor="roomName" className="block text-sm font-bold text-primary mb-2">
                  Room Name
                </label>
                <input
                  type="text"
                  id="roomName"
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                  placeholder="My awesome room"
                  className="input"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
              >
                {loading ? 'Creating...' : 'Create Room'}
              </button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-default" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-card text-tertiary">Or</span>
              </div>
            </div>

            <button
              onClick={() => navigate('/join')}
              className="btn-secondary w-full"
            >
              Join with Room Code
            </button>
          </div>

          {/* My Rooms Card */}
          <div className="card">
            <h2 className="text-xl font-bold text-primary mb-4">
              My Rooms
            </h2>

            {loadingRooms ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-lime-400" />
              </div>
            ) : myRooms.length === 0 ? (
              <p className="text-tertiary text-center py-8">
                You haven't joined any rooms yet
              </p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {myRooms.map((room) => (
                  <div
                    key={room.roomId}
                    className="flex items-center justify-between p-3 rounded-lg cursor-pointer transition bg-muted hover:opacity-80"
                    onClick={() => handleRoomClick(room)}
                  >
                    <div className="min-w-0 flex-1">
                      <p className={`font-bold truncate ${room.status === 'closed' ? 'text-tertiary' : 'text-primary'}`}>
                        {room.roomName}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`badge ${getRoleBadgeClass(room.role)}`}>
                          {getRoleDisplay(room.role)}
                        </span>
                        {room.status === 'closed' && room.role === 'owner' && (
                          <span className="text-xs text-tertiary">(Click to reopen)</span>
                        )}
                      </div>
                    </div>
                    <span className={`badge flex-shrink-0 ${
                      room.status === 'open'
                        ? 'badge-lime'
                        : 'badge-navy'
                    }`}>
                      {room.status === 'open' ? 'Open' : 'Closed'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
